import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useBreachCheck } from "../app/preferences.js";
import { useSession } from "../app/session.js";
import { breachChecker } from "../lib/breach.js";
import { healthInputs } from "../lib/entries.js";
import type { Entry } from "../lib/entries.js";
import type { HealthInput } from "../lib/health.js";

const STALE_MS = 6 * 60 * 60 * 1000;

export type BreachStatus = "off" | "checking" | "error" | "done";

export interface BreachScan {
  readonly status: BreachStatus;
  /** Entry ids whose password appears in known breaches. */
  readonly breached: ReadonlySet<string>;
  /** Breach count per entry id (only breached entries). */
  readonly counts: ReadonlyMap<string, number>;
  readonly error: string | undefined;
}

const EMPTY_IDS: ReadonlySet<string> = new Set<string>();
const EMPTY_COUNTS: ReadonlyMap<string, number> = new Map<string, number>();

/**
 * Checks every login password against Have I Been Pwned. The query key holds only ids and change
 * times, never passwords or hashes; the cache is wiped when the vault locks.
 */
export function useBreachScan(entries: ReadonlyArray<Entry>): BreachScan {
  const enabled = useBreachCheck();
  const unlocked = useSession().state.status === "unlocked";
  const inputs = useMemo(
    (): ReadonlyArray<HealthInput> => healthInputs(entries).filter((i: HealthInput): boolean => i.password !== ""),
    [entries]
  );
  const signature = inputs.map((i: HealthInput): string => `${i.id}:${i.updatedAt}`).sort().join(",");

  const query = useQuery<ReadonlyMap<string, number>, Error>({
    queryKey: ["breach", signature],
    queryFn: async (): Promise<ReadonlyMap<string, number>> => {
      const byPassword = await breachChecker.countMany(inputs.map((i: HealthInput): string => i.password));
      const counts = new Map<string, number>();
      for (const input of inputs) {
        const count = byPassword.get(input.password) ?? 0;
        if (count > 0) {
          counts.set(input.id, count);
        }
      }
      return counts;
    },
    enabled: enabled && unlocked && inputs.length > 0,
    staleTime: STALE_MS,
    retry: false,
    refetchOnWindowFocus: false
  });

  return useMemo((): BreachScan => {
    if (!enabled) {
      return { status: "off", breached: EMPTY_IDS, counts: EMPTY_COUNTS, error: undefined };
    }
    if (inputs.length === 0) {
      return { status: "done", breached: EMPTY_IDS, counts: EMPTY_COUNTS, error: undefined };
    }
    if (query.data !== undefined) {
      return { status: "done", breached: new Set(query.data.keys()), counts: query.data, error: undefined };
    }
    if (query.error !== null) {
      return { status: "error", breached: EMPTY_IDS, counts: EMPTY_COUNTS, error: query.error.message };
    }
    return { status: "checking", breached: EMPTY_IDS, counts: EMPTY_COUNTS, error: undefined };
  }, [enabled, inputs.length, query.data, query.error]);
}
